"use client";

import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from "react";
import { cn } from "@/lib/utils";
import { addDaysISO } from "@/lib/study-plan";
import type { ScheduledTask } from "@/lib/study-plan-state";
import TaskCard, { type MoveTarget, type TaskCardActions } from "./TaskCard";
import {
  WEEKDAY_KO,
  WINDOW_DAYS,
  dateFromISO,
  isExamKind,
  weekdayOf,
} from "./schedule-shared";
import {
  dropInsertIndex,
  InsertionLine,
  useFlipReorder,
  type DropIndicator,
} from "./schedule-dnd";

/**
 * 주(Week) 뷰 보드 — 5일 슬라이딩 윈도우 (JCG-2310).
 *
 * - 데스크탑(sm+): 5개 day 칼럼 가로 배치(`grid-cols-5`). 고정 7일(캘린더 주)이 아니라
 *   `windowStartISO` 부터 5일. 칼럼이 넓어져 제목 가독성이 좋아진다. 각 칼럼은 `data-day-column`(날짜)
 *   마커를 달고, task 카드를 다른 칼럼으로 끌어다 놓으면 부모(ScheduleCalendar)의 DragEnd 핸들러가
 *   커서 좌표(elementsFromPoint)로 대상 칼럼을 찾아 moveTask 한다. 빈 칼럼도 drop 대상.
 * - 윈도우 이동: 헤더 ‹ › 화살표(±5일)·"오늘" 버튼, 또는 보드를 좌우로 스와이프/휠 — 끄는 동안
 *   칼럼폭마다 1일씩 실시간 이동. 트랙패드 가로 스와이프(wheel)는 보드 어디서나, 마우스 드래그/터치
 *   (pointer)는 카드 위에서 시작하면 카드 DnD 우선.
 * - 모바일(<sm): 세로 아젠다(요일별 섹션 스택). 5칼럼이 안 들어가므로 카드 "⋯ → 다른 날로 이동"
 *   액션시트가 DnD 대안. 상단 요일 칩으로 해당 섹션에 점프.
 *
 * windowStartISO 는 윈도우 시작일(일요일 스냅 없음). tasksByDate 는 effectiveDateISO 로 그룹된 task 맵.
 */
export default function WeekBoard({
  windowStartISO,
  todayISO,
  tasksByDate,
  moveTargets,
  actions,
  dropIndicator,
  flipExcludeRef,
  onShiftDays,
  dragging,
  readOnly,
}: {
  windowStartISO: string;
  todayISO: string;
  tasksByDate: Map<string, ScheduledTask[]>;
  moveTargets: MoveTarget[];
  actions: TaskCardActions;
  /** 드래그 중 드롭 위치(칼럼 하이라이트 + 삽입 인디케이터). */
  dropIndicator: DropIndicator | null;
  /** 직전 드롭 카드(FLIP 슬라이드 제외). 부모가 관리, 칼럼들이 공유. */
  flipExcludeRef: RefObject<string | null>;
  /** 보드 스와이프로 윈도우를 days(±)만큼 이동. 부모가 clamp 처리. */
  onShiftDays: (days: number) => void;
  /** 드래그 중 여부 — 가장자리 자동이동 시에만 칼럼 가로 슬라이드(휠/스와이프엔 즉시 반영). */
  dragging: boolean;
  /** 이전 회차 조회 모드. 날짜 탐색은 유지하되 task 변경·DnD 진입점은 제거한다. */
  readOnly: boolean;
}) {
  const days = useMemo(
    () =>
      Array.from({ length: WINDOW_DAYS }, (_, i) =>
        addDaysISO(windowStartISO, i),
      ),
    [windowStartISO],
  );

  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // ── 보드 스와이프/휠 네비게이션(데스크탑) — 끄는 동안 칼럼폭마다 1일씩 실시간 이동 ──
  // (1) 트랙패드 2-finger 가로 스와이프 = wheel(deltaX). 보드 어디서나(카드 위 포함) 동작, 가로일 때만
  //     preventDefault 로 브라우저 뒤로가기 차단. (2) 마우스 드래그/터치 = pointer. 카드 위에서 시작한
  //     제스처는 카드 DnD 우선(무시). 오른쪽으로 끌면 과거(이전 날짜), 왼쪽으로 끌면 미래.
  const swipeRef = useRef<HTMLDivElement | null>(null);
  const swipe = useRef<{
    pointerId: number;
    startX: number;
    colWidth: number;
    applied: number; // 지금까지 적용한 누적 이동(일) — 드래그 중 증분만 shift
  } | null>(null);

  // 휠(트랙패드 가로 스와이프): React onWheel 은 passive 라 preventDefault 가 안 먹어, 네이티브
  // 리스너를 passive:false 로 직접 단다. onShiftDays 최신값은 ref 로 참조(리스너 재바인딩 방지).
  const onShiftRef = useRef(onShiftDays);
  useEffect(() => {
    onShiftRef.current = onShiftDays;
  });
  useEffect(() => {
    const grid = swipeRef.current;
    if (!grid) return;
    let accum = 0;
    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return; // 세로 의도 → 페이지 스크롤 양보
      e.preventDefault();
      const colWidth = grid.getBoundingClientRect().width / WINDOW_DAYS;
      if (colWidth <= 0) return;
      accum += e.deltaX;
      while (accum >= colWidth) {
        onShiftRef.current(1);
        accum -= colWidth;
      }
      while (accum <= -colWidth) {
        onShiftRef.current(-1);
        accum += colWidth;
      }
    };
    grid.addEventListener("wheel", onWheel, { passive: false });
    return () => grid.removeEventListener("wheel", onWheel);
  }, []);

  const onSwipeDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return; // 주 버튼만
    // portal 로 그려진 요소(메뉴·팝오버 등)의 포인터는 보드 DOM 밖이므로 스와이프로 잡지 않는다.
    // React 트리로는 올라오지만, 여기서 캡처하면 그 요소가 click 을 못 받는다.
    if (!e.currentTarget.contains(e.target as Node)) return;
    if ((e.target as HTMLElement).closest("[data-task-uid]")) return; // 카드 위 → DnD 우선
    const grid = swipeRef.current;
    if (!grid) return;
    const colWidth = grid.getBoundingClientRect().width / WINDOW_DAYS;
    if (colWidth <= 0) return;
    swipe.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      colWidth,
      applied: 0,
    };
    grid.setPointerCapture(e.pointerId); // 카드 위로 끌어도 끝까지 추적
  };
  const onSwipeMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const g = swipe.current;
    if (!g || g.pointerId !== e.pointerId) return;
    // 오른쪽으로 끌수록(dx>0) 과거(음수). 끈 거리/칼럼폭 만큼 실시간 이동, 증분만 적용.
    const desired = -Math.round((e.clientX - g.startX) / g.colWidth);
    if (desired !== g.applied) {
      onShiftDays(desired - g.applied);
      g.applied = desired;
    }
  };
  const endSwipe = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (swipe.current?.pointerId === e.pointerId) swipe.current = null;
  };

  // 가장자리 자동이동(드래그 중)으로 윈도우가 1일씩 밀릴 때 칼럼이 가로로 미끄러진다(FLIP, translateX).
  // freeze 는 세로 중점이라 가로 슬라이드와 충돌 없음. 휠/스와이프(비드래그)엔 enabled=false 로 즉시 반영.
  useFlipReorder(swipeRef, days.join(","), {
    axis: "x",
    attr: "data-day-column",
    enabled: dragging,
  });

  return (
    <div>
      {/* 모바일 요일 칩(섹션 점프) */}
      <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1 sm:hidden">
        {days.map((iso) => {
          const tasks = tasksByDate.get(iso) ?? [];
          const isToday = iso === todayISO;
          return (
            <button
              key={iso}
              type="button"
              onClick={() =>
                sectionRefs.current[iso]?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                })
              }
              className={cn(
                "flex shrink-0 flex-col items-center rounded-lg border px-2.5 py-1 text-[11px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
                isToday
                  ? "border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                  : "border-border text-muted-foreground hover:bg-accent",
              )}
            >
              <span className="font-semibold">
                {WEEKDAY_KO[weekdayOf(iso)]}
              </span>
              <span className="tabular-nums">
                {dateFromISO(iso).getUTCDate()}
              </span>
              {tasks.length > 0 && (
                <span className="mt-0.5 h-1 w-1 rounded-full bg-amber-500 dark:bg-amber-400" />
              )}
            </button>
          );
        })}
      </div>

      {/* 데스크탑: 5칼럼 그리드. 배경/헤더/빈 공간 좌우 스와이프로도 윈도우 이동(카드 위는 DnD). */}
      <div
        ref={swipeRef}
        onPointerDown={onSwipeDown}
        onPointerMove={onSwipeMove}
        onPointerUp={endSwipe}
        onPointerCancel={endSwipe}
        className="hidden grid-cols-5 gap-2 sm:grid"
      >
        {days.map((iso) => (
          <DayColumn
            key={iso}
            dateISO={iso}
            todayISO={todayISO}
            tasks={tasksByDate.get(iso) ?? []}
            moveTargets={moveTargets}
            actions={actions}
            dropIndicator={dropIndicator}
            flipExcludeRef={flipExcludeRef}
            readOnly={readOnly}
          />
        ))}
      </div>

      {/* 모바일: 세로 아젠다 */}
      <div className="space-y-3 sm:hidden">
        {days.map((iso) => (
          <AgendaSection
            key={iso}
            setRef={(el) => {
              sectionRefs.current[iso] = el;
            }}
            dateISO={iso}
            todayISO={todayISO}
            tasks={tasksByDate.get(iso) ?? []}
            moveTargets={moveTargets}
            actions={actions}
            readOnly={readOnly}
          />
        ))}
      </div>
    </div>
  );
}

/** 데스크탑 day 칼럼 — drop 대상(data-day-column 마커). */
function DayColumn({
  dateISO,
  todayISO,
  tasks,
  moveTargets,
  actions,
  dropIndicator,
  flipExcludeRef,
  readOnly,
}: {
  dateISO: string;
  todayISO: string;
  tasks: ScheduledTask[];
  moveTargets: MoveTarget[];
  actions: TaskCardActions;
  dropIndicator: DropIndicator | null;
  flipExcludeRef: RefObject<string | null>;
  readOnly: boolean;
}) {
  const insertIndex = readOnly
    ? null
    : dropInsertIndex(tasks, dropIndicator, dateISO);
  const isOver = insertIndex !== null;
  // 재정렬 슬라이드(FLIP): 이 칼럼 task 순서가 바뀌면 카드가 새 자리로 미끄러진다.
  const listRef = useRef<HTMLDivElement>(null);
  useFlipReorder(listRef, tasks.map((t) => t.uid).join(","), {
    excludeUidRef: flipExcludeRef,
  });
  const isToday = dateISO === todayISO;
  const d = dateFromISO(dateISO);
  const hasExam = tasks.some((t) => isExamKind(t.kind));
  const doneCount = tasks.filter((t) => t.done && !t.skipped).length;
  const totalCount = tasks.filter((t) => !t.skipped).length;

  return (
    <div
      data-day-column={dateISO}
      className={cn(
        "flex min-h-[8rem] flex-col rounded-xl border transition-colors",
        isToday ? "border-amber-400 dark:border-amber-500" : "border-border",
        hasExam && "bg-amber-50/40 dark:bg-amber-950/20",
        isOver &&
          "border-amber-500 bg-amber-100/60 ring-2 ring-amber-400/50 dark:bg-amber-900/30",
      )}
    >
      {/* 칼럼 헤더 */}
      <div
        className={cn(
          "flex items-center justify-between gap-1 rounded-t-xl px-2 py-1.5 text-[11px]",
          isToday
            ? "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
            : "text-muted-foreground",
        )}
      >
        <span className="flex items-center gap-1 font-semibold">
          <span>{WEEKDAY_KO[d.getUTCDay()]}</span>
          <span className="tabular-nums">{d.getUTCDate()}</span>
          {isToday && (
            <span className="rounded-full bg-amber-500 px-1.5 py-px text-[9px] font-bold text-white dark:bg-amber-400 dark:text-amber-950">
              오늘
            </span>
          )}
        </span>
        {totalCount > 0 && (
          <span className="tabular-nums text-muted-foreground/80">
            {doneCount}/{totalCount}
          </span>
        )}
      </div>

      {/* task 목록(빈 칼럼도 drop 대상 — 최소 높이 유지). 드래그 중 삽입 위치에 인디케이터 라인 표시. */}
      <div ref={listRef} className="flex flex-1 flex-col gap-1.5 p-1.5">
        {tasks.length > 0 ? (
          <>
            {tasks.map((task, i) => (
              <Fragment key={task.uid}>
                {insertIndex === i && <InsertionLine />}
                {/* data-flip-uid: 재정렬 시 이 래퍼가 새 위치로 미끄러진다(FLIP). */}
                <div data-flip-uid={task.uid}>
                  <TaskCard
                    task={task}
                    actions={actions}
                    moveTargets={moveTargets}
                    draggable={!readOnly}
                    readOnly={readOnly}
                  />
                </div>
              </Fragment>
            ))}
            {insertIndex === tasks.length && <InsertionLine />}
          </>
        ) : (
          <div
            className={cn(
              "flex flex-1 items-center justify-center rounded-lg py-3 text-[10px] transition-colors",
              isOver
                ? "border border-dashed border-amber-400 text-amber-600 dark:text-amber-400"
                : "text-muted-foreground/50",
            )}
          >
            {isOver ? "여기에 놓기" : "—"}
          </div>
        )}
      </div>
    </div>
  );
}

/** 모바일 아젠다 섹션 — 요일별 세로 스택. setRef 콜백으로 부모가 섹션 점프용 DOM 노드를 잡는다. */
function AgendaSection({
  setRef,
  dateISO,
  todayISO,
  tasks,
  moveTargets,
  actions,
  readOnly,
}: {
  setRef: (el: HTMLDivElement | null) => void;
  dateISO: string;
  todayISO: string;
  tasks: ScheduledTask[];
  moveTargets: MoveTarget[];
  actions: TaskCardActions;
  readOnly: boolean;
}) {
  const isToday = dateISO === todayISO;
  const d = dateFromISO(dateISO);
  const hasExam = tasks.some((t) => isExamKind(t.kind));
  const doneCount = tasks.filter((t) => t.done && !t.skipped).length;
  const totalCount = tasks.filter((t) => !t.skipped).length;

  return (
    <section
      ref={setRef}
      className={cn(
        "scroll-mt-4 rounded-xl border p-2.5",
        isToday ? "border-amber-400 dark:border-amber-500" : "border-border",
        hasExam && "bg-amber-50/40 dark:bg-amber-950/20",
      )}
    >
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
          <span>
            {d.getUTCMonth() + 1}/{d.getUTCDate()}
          </span>
          <span className="text-muted-foreground">
            ({WEEKDAY_KO[d.getUTCDay()]})
          </span>
          {isToday && (
            <span className="rounded-full bg-amber-500 px-1.5 py-px text-[10px] font-bold text-white dark:bg-amber-400 dark:text-amber-950">
              오늘
            </span>
          )}
        </h3>
        {totalCount > 0 && (
          <span className="text-[11px] font-medium text-muted-foreground tabular-nums">
            {doneCount}/{totalCount} 완료
          </span>
        )}
      </div>
      {tasks.length > 0 ? (
        <div className="space-y-1.5">
          {tasks.map((task) => (
            <TaskCard
              key={task.uid}
              task={task}
              actions={actions}
              moveTargets={moveTargets}
              readOnly={readOnly}
            />
          ))}
        </div>
      ) : (
        <p className="py-2 text-center text-xs text-muted-foreground/60">
          예정된 학습이 없어요
        </p>
      )}
    </section>
  );
}
